import { Facility } from '@reliance/types/models/Facility';
import { SpecialCondition } from '@reliance/types/models/SpecialCondition';
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import { alertStore } from '../alert/alertStore';
import { downloadCSV } from '../common/csv';
import { formatDate } from '../format/DateTime';
import { formatPhoneNumber, toE164 } from '../format/PhoneNumber';
import { useFacilities } from '../hooks/Facility';
import { SpecialConditionListFilters, useSpecialConditions } from '../hooks/SpecialCondition';
import { useNotifyTabLoading } from '../hooks/useTabLoadingReporter';
import BaseHtmlTable from '../table/BaseHtmlTable';
import dateRange from '../table/filters/dateRange';
import { useServerTableFilterState } from '../table/useServerTableFilterState';
import AddEditModal from './AddEditModal';
import RemoveModal from './RemoveModal';

type FacilityListProps = {
  facilityId: number;
  facility?: Facility;
  noShow?: string[];
  showDelete?: boolean;
};

type ConditionIndexRow = SpecialCondition & {
  ani?: string;
  facilityName?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  phoneNumber?: string;
};

const DEFAULT_ORDER_BY = '-created';

function showColumn(noShow: string[] | undefined, column: string): boolean {
  return noShow == null || noShow.indexOf(column) === -1;
}

function formatAccountName(row: ConditionIndexRow): string {
  if (row.companyName) {
    return row.companyName;
  }
  const parts = [row.firstName, row.lastName].filter(Boolean);
  if (parts.length > 0) {
    return parts.join(' ');
  }
  if (row.phoneNumber) {
    return formatPhoneNumber(row.phoneNumber);
  }
  return '';
}

const FacilitySpecialConditionsList: React.FC<FacilityListProps> = (props) => {
  const { facilityId } = props;
  const noShow = props.noShow ?? [];
  const noShowDepKey = noShow.join('|');
  const showDelete = props.showDelete !== false;

  const { result: facilitiesList } = useFacilities();
  const facilities = facilitiesList ?? [];
  const [removeRow, setRemoveRow] = useState<ConditionIndexRow | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const facilityForContext = useMemo(() => {
    if (props.facility) {
      return props.facility;
    }
    return facilities.find((f) => f.id === facilityId);
  }, [props.facility, facilities, facilityId]);

  const initialColumnFilters = useMemo(
    () => [
      {
        id: 'created',
        value: {
          startDate: moment().subtract(10, 'year').format('YYYY/MM/DD'),
          endDate: moment().endOf('day').format('YYYY/MM/DD'),
        },
      },
    ],
    []
  );

  const queryColumnDefs = useMemo(() => {
    const cols: { id: string; meta?: Record<string, unknown> }[] = [{ id: 'id', meta: { sortName: 'id' } }];
    if (showColumn(noShow, 'facility')) {
      cols.push({ id: 'facilityTitle', meta: { sortName: 'facilityName' } });
    }
    cols.push(
      { id: 'created', meta: { filterComponent: 'DateRangeFilter', sortName: 'created' } },
      { id: 'enteredBy', meta: { sortName: 'enteredBy' } },
      { id: 'condition', meta: { sortName: 'condition' } }
    );
    if (showColumn(noShow, 'ani')) {
      cols.push({ id: 'ani', meta: { sortName: 'ani' } });
    }
    cols.push(
      { id: 'accountName', meta: { sortName: 'accountName' } },
      { id: 'description', meta: { sortName: 'description' } },
      { id: 'options' }
    );
    return cols;
  }, [noShowDepKey]);

  const baseRecord = useMemo(() => ({ orderBy: DEFAULT_ORDER_BY }) as Record<string, unknown>, []);
  const tableState = useServerTableFilterState(queryColumnDefs, baseRecord, {
    initialPagination: { pageIndex: 0, pageSize: 200 },
    initialSorting: [{ id: 'created', desc: true }],
    initialColumnFilters,
  });

  const filters = useMemo((): SpecialConditionListFilters => {
    const merged = tableState.mergedRecord;
    const created = merged.created as { startDate?: string; endDate?: string } | undefined;
    let startDate: string;
    let endDate: string;
    if (created?.startDate && created?.endDate) {
      startDate = created.startDate.replace(/\//g, '-') + ' 00:00:00';
      endDate = created.endDate.replace(/\//g, '-') + ' 23:59:59';
    } else {
      startDate = moment().subtract(10, 'year').format('YYYY-MM-DD 00:00:00');
      endDate = moment().endOf('day').format('YYYY-MM-DD HH:mm:ss');
    }
    const idParsed = parseInt(String(merged.id ?? '').trim(), 10);
    const out: SpecialConditionListFilters = {
      page: merged.page as number,
      perPage: merged.perPage as number,
      orderBy: (merged.orderBy as string | undefined) ?? DEFAULT_ORDER_BY,
      facilityId,
      startDate,
      endDate,
    };
    if (Number.isFinite(idParsed) && idParsed > 0) {
      out.id = idParsed;
    }
    const condition = merged.condition as string | undefined;
    const ani = merged.ani as string | undefined;
    const accountName = merged.accountName as string | undefined;
    const description = merged.description as string | undefined;
    if (condition != null && condition.trim() !== '') {
      out.condition = condition.trim();
    }
    if (ani != null && ani.trim() !== '') {
      out.ani = ani.trim();
    }
    if (accountName != null && accountName.trim() !== '') {
      out.accountName = accountName.trim();
    }
    if (description != null && description.trim() !== '') {
      out.description = description.trim();
    }
    return out;
  }, [tableState.mergedRecord, facilityId]);

  const conditionsResult = useSpecialConditions(filters);

  useNotifyTabLoading('special-conditions', conditionsResult.loading);

  useEffect(() => {
    if (conditionsResult.error) {
      alertStore.error('An error occurred while loading the special conditions');
    }
  }, [conditionsResult.error]);

  const rows = (conditionsResult.result?.results ?? []) as ConditionIndexRow[];
  const total = conditionsResult.result?.total ?? 0;
  const perPage = conditionsResult.result?.perPage ?? tableState.pagination.pageSize;
  const totalPages = Math.ceil(total / perPage) || 1;
  const page = conditionsResult.result?.page ?? tableState.pagination.pageIndex + 1;

  const columnHelper = useMemo(() => createColumnHelper<ConditionIndexRow>(), []);

  const columnDefs = useMemo((): ColumnDef<ConditionIndexRow>[] => {
    const cols: ColumnDef<ConditionIndexRow>[] = [
      columnHelper.accessor('id', {
        id: 'id',
        header: 'ID',
        meta: { sortName: 'id' },
      }),
    ];

    if (showColumn(noShow, 'facility')) {
      cols.push(
        columnHelper.accessor((row) => row.facilityName ?? row.facility?.name ?? '', {
          id: 'facilityTitle',
          header: 'Facility',
          meta: { sortName: 'facilityName' },
          enableColumnFilter: false,
        })
      );
    }

    cols.push(
      columnHelper.accessor((row) => row.created, {
        id: 'created',
        header: 'Date',
        filterFn: dateRange,
        meta: {
          filterComponent: 'DateRangeFilter',
          sortName: 'created',
        },
        cell: (info) => formatDate(info.getValue()),
        enableColumnFilter: true,
      }),
      columnHelper.accessor((row) => (typeof row.enteredBy === 'string' ? row.enteredBy : ''), {
        id: 'enteredBy',
        header: 'Created By',
        meta: { sortName: 'enteredBy' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => String(row.condition ?? ''), {
        id: 'condition',
        header: 'Condition',
        meta: { sortName: 'condition' },
      })
    );

    if (showColumn(noShow, 'ani')) {
      cols.push(
        columnHelper.accessor((row) => row.ani ?? '', {
          id: 'ani',
          header: 'ANI',
          meta: { sortName: 'ani' },
          cell: (info) => {
            const row = info.row.original;
            const displaySource = (row.phoneNumber ?? row.ani ?? '').trim();
            if (!displaySource) {
              return '';
            }
            const e164 = toE164(row.phoneNumber || row.ani || '');
            if (!e164) {
              return formatPhoneNumber(displaySource);
            }
            return (
              <Link to={`/account/${encodeURIComponent(e164)}`}>{formatPhoneNumber(displaySource)}</Link>
            );
          },
        })
      );
    }

    cols.push(
      columnHelper.accessor((row) => formatAccountName(row), {
        id: 'accountName',
        header: 'Account',
        meta: { sortName: 'accountName' },
      }),
      columnHelper.accessor('description', {
        id: 'description',
        header: 'Description',
        meta: { sortName: 'description' },
      }),
      columnHelper.display({
        id: 'options',
        header: 'Options',
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          return (
            <>
              <Button
                variant="default"
                size="sm"
                className="me-1"
                onClick={() => downloadCSV([row as unknown as object])}>
                <i className="fas fa-download" />
              </Button>
              {showDelete && (
                <Button variant="danger" size="sm" onClick={() => setRemoveRow(row)}>
                  <i className="fas fa-trash-alt" />
                </Button>
              )}
            </>
          );
        },
      })
    );

    return cols;
  }, [columnHelper, noShowDepKey, showDelete]);

  return (
    <>
      <div className="row">
        <div className="col-md-12">
          <Button variant="primary" className="btn-labeled me-2" onClick={() => setShowAdd(true)}>
            <span className="btn-label">
              <i className="fas fa-plus-circle" />
            </span>
            Add Condition
          </Button>
          {rows.length > 0 && (
            <Button variant="secondary" onClick={() => downloadCSV(rows as object[])}>
              Download All
            </Button>
          )}
        </div>
      </div>
      <div className="row mt-2">
        <div className="col-md-12">
          <BaseHtmlTable
            rows={rows}
            columnDefs={columnDefs}
            page={page}
            totalPages={totalPages}
            total={total}
            perPage={perPage}
            serverPagination={tableState.serverPagination}
            onServerPaginationChange={tableState.onServerPaginationChange}
            serverSorting={tableState.serverSorting}
            onServerSortingChange={tableState.onServerSortingChange}
            serverColumnFilters={tableState.serverColumnFilters}
            onServerColumnFiltersChange={tableState.onServerColumnFiltersChange}
            paginationMode="server"
            loading={conditionsResult.loading}
            pageSizeOptions={[100, 200, 500, 1000, 2000]}
          />
        </div>
      </div>

      {removeRow && (
        <RemoveModal
          condition={removeRow as SpecialCondition}
          onClose={(success) => {
            setRemoveRow(null);
            if (success) {
              conditionsResult.execute(filters);
            }
          }}
        />
      )}

      {showAdd && facilityForContext && facilities.length > 0 && (
        <AddEditModal
          facilities={facilities}
          facility={facilityForContext}
          onClose={(success) => {
            setShowAdd(false);
            if (success) {
              conditionsResult.execute(filters);
            }
          }}
        />
      )}
    </>
  );
};

export default FacilitySpecialConditionsList;
